import { useEffect, useRef } from 'react';

import parseISO from 'date-fns/parseISO';
import differenceInMilliseconds from 'date-fns/differenceInMilliseconds';

const SAFE_SET_TIMEOUT_MAX_VALUE = 2147483647;
export const getCurrentDateInISO = () => parseISO(new Date().toISOString());
const getCallbackTriggerInISO = (callbackTriggerTime) => parseISO(callbackTriggerTime);
export const getTimeUntilThreshold = (callbackTriggerTime, threshold = 0) => {
    const timeDifference = differenceInMilliseconds(
        getCallbackTriggerInISO(callbackTriggerTime),
        getCurrentDateInISO(),
    );

    const timeUntilThreshold = timeDifference - threshold;
    // We are putting bounds on the timeouts to prevent the setTimeout from crashing
    // https://developer.mozilla.org/en-US/docs/Web/API/setTimeout#maximum_delay_value
    const safeTimeUntilThreshold = Math.max(Math.min(timeUntilThreshold, SAFE_SET_TIMEOUT_MAX_VALUE), 0);
    return safeTimeUntilThreshold;
};

const useWaitUntil = (
    callback, // it is a performance best practice to wrap the passed function in useCallback
    callbackTriggerTime,
    { threshold = 0 } = {},
) => {
    const timerId = useRef(null);

    useEffect(() => {
        timerId.current && clearInterval(timerId.current);

        if (callbackTriggerTime) {
            const timeUntilThreshold = getTimeUntilThreshold(callbackTriggerTime, threshold);
            timerId.current = setTimeout(callback, timeUntilThreshold);
        }

        return () => timerId.current && clearInterval(timerId.current);
    }, [callback, callbackTriggerTime, threshold]);
};

export default useWaitUntil;
